<template>
  <div>
    <nav aria-label="Page navigation">
      <ul class="pagination">
        <li v-if="showFirstLastPageNavigation" class="page-item" :class="{ disabled: isFirstPage }">
          <a
            id="showFirstPage"
            class="page-link"
            aria-label="Go to first page"
            @click="onClickFirstPage"
            ><ips-icon icon="ico-double-chevron-left"></ips-icon
          ></a>
        </li>
        <li class="page-item" :class="{ disabled: isFirstPage }">
          <a
            id="showPreviousPage"
            class="page-link"
            aria-label="Go to previous page"
            @click="onClickPreviousPage"
            ><ips-icon icon="ico-chevron-left"></ips-icon
          ></a>
        </li>
        <li
          v-for="page in pages"
          :key="page.id"
          class="page-item"
          :class="{ active: isCurrentPage(page.id) }"
        >
          <a
            :id="'showPage' + page.id"
            class="page-link"
            :aria-label="`Go to page ${page.id}`"
            @click="onClickCurrentPage(page.id)"
          >
            {{ page.id }}
          </a>
        </li>
        <li class="page-item" :class="{ disabled: isLastPage }">
          <a
            id="showNextPage"
            class="page-link"
            aria-label="Go to next page"
            @click="onClickNextPage"
            ><ips-icon icon="ico-chevron-right"></ips-icon
          ></a>
        </li>
        <li v-if="showFirstLastPageNavigation" class="page-item" :class="{ disabled: isLastPage }">
          <a
            id="showLastPage"
            class="page-link"
            aria-label="Go to last page"
            @click="onClickLastPage"
            ><ips-icon icon="ico-double-chevron-right"></ips-icon
          ></a>
        </li>
        <li class="page-item">
          <ips-select
            :id="id"
            label="Pagination"
            :value="selectedPerPageOptionValue"
            :display-label="false"
            @select="onSelectPerPageOption"
          >
            <template slot="options">
              <option
                v-for="(opt, i) in perPageOptions"
                :key="i"
                :value="opt.value"
                :label="opt.label"
              >
                {{ opt.label }}
              </option>
            </template>
          </ips-select>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import IpsIcon from '@ipscape/ips-icon';
import IpsSelect from '@ipscape/ips-select';

export default {
  name: 'IpsPagination',
  components: { IpsIcon, IpsSelect },
  props: {
    id: {
      name: 'id',
      description: 'The id of the pagination',
      type: String,
      default: 'pagination',
      required: false,
    },
    maxPagesVisible: {
      name: 'maxPagesVisible',
      description: 'Maximum pages visible',
      type: Number,
      default: 5,
      required: false,
    },
    selectedPage: {
      name: 'selectedPage',
      description: 'The selected page',
      type: Number,
      default: 1,
      required: false,
    },
    totalItems: {
      name: 'totalItems',
      description: 'Total items',
      type: Number,
      default: 1,
      required: true,
    },
  },
  data() {
    return {
      currentPage: this.selectedPage,
      perPageOptions: [
        { value: 10, label: '10 / page' },
        { value: 20, label: '20 / page' },
        { value: 50, label: '50 / page' },
        { value: 100, label: '100 / page' },
      ],
      selectedPerPageOptionValue: 20,
    };
  },
  computed: {
    pages() {
      let pages = [];

      for (let i = 1; i <= this.totalPages; i++) {
        if (
          i > this.currentPage - this.maxPagesVisible &&
          i < this.currentPage + this.maxPagesVisible
        ) {
          pages.push({ id: i });
          if (pages.length > this.maxPagesVisible) {
            pages.shift();
          }
        }
      }
      return pages;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.selectedPerPageOptionValue);
    },
    showFirstLastPageNavigation() {
      return this.totalPages > this.maxPagesVisible;
    },
    isFirstPage() {
      return this.currentPage === 1;
    },
    isLastPage() {
      return this.currentPage === this.totalPages;
    },
  },
  methods: {
    isCurrentPage(page) {
      return this.currentPage === page;
    },
    onSelectPerPageOption(newValue) {
      this.selectedPerPageOptionValue = newValue;
      this.currentPage = 1;
      this.$emit('pagechanged', this.currentPage);
    },
    onClickCurrentPage(page) {
      this.currentPage = page;
      this.$emit('pagechanged', this.currentPage);
    },
    onClickPreviousPage() {
      this.currentPage = this.currentPage - 1;
      this.$emit('pagechanged', this.currentPage);
    },
    onClickNextPage() {
      this.currentPage = this.currentPage + 1;
      this.$emit('pagechanged', this.currentPage);
    },
    onClickFirstPage() {
      this.currentPage = 1;
      this.$emit('pagechanged', 1);
    },
    onClickLastPage() {
      this.currentPage = this.totalPages;
      this.$emit('pagechanged', this.currentPage);
    },
  },
};
</script>

<style lang="scss">
@import 'IpsPagination';
</style>
